import { type StepForm, WizardField } from '../WizardField';

/** Provenance of a prefilled field vs the state seed (Task 1). */
export type FieldOrigin = 'default' | 'custom';

interface Props {
  form: StepForm;
  /**
   * Per-field provenance keyed by the bare step-6 field name (e.g.
   * `salesTaxRate`). Absent key = no chip (unknown / not seed-backed).
   */
  origins?: Record<string, FieldOrigin>;
  /** Label for the "matches seed" chip, e.g. "TX default" or "US default". */
  seedLabel?: string;
}

export function Step6Jurisdiction({ form, origins, seedLabel }: Props) {
  const specialDistrictName = form.watch('step6.specialDistrictName') as string | undefined;

  // Build the cosmetic provenance pill for a seed-backed field. Returns null
  // when we have no origin data (e.g. re-run edit, or profile not loaded).
  const chip = (key: string): { label: string; tone: FieldOrigin } | null => {
    const origin = origins?.[key];
    if (!origin) return null;
    return origin === 'default'
      ? { label: seedLabel ?? 'State default', tone: 'default' }
      : { label: 'Customized', tone: 'custom' };
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-800">Jurisdiction & Tax Rates</h3>
        <p className="mt-0.5 text-sm text-slate-500">
          City tax rates and cost-of-services assumptions from your jurisdiction profile.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Tax rates
        </p>

        <WizardField
          form={form}
          name="step6.salesTaxRate"
          label="City sales tax rate"
          format="percent"
          min={0}
          max={0.1}
          step={0.001}
          hint="City share only"
          originChip={chip('salesTaxRate')}
        />

        <WizardField
          form={form}
          name="step6.propertyTaxRate"
          label="City property tax rate"
          format="percent"
          min={0}
          max={0.05}
          step={0.0001}
          hint="Effective rate per $100 of assessed value"
          originChip={chip('propertyTaxRate')}
        />

        <WizardField
          form={form}
          name="step6.hotelOccupancyTaxRate"
          label="Hotel Occupancy Tax (HOT) rate"
          format="percent"
          min={0}
          max={0.25}
          step={0.005}
          originChip={chip('hotelOccupancyTaxRate')}
        />

        <WizardField
          form={form}
          name="step6.mixedBeverageShareRate"
          label="Mixed beverage city share rate"
          format="percent"
          min={0}
          max={0.25}
          step={0.001}
          originChip={chip('mixedBeverageShareRate')}
        />

        {/* ADR-034 2.1: local wage/income tax */}
        <p className="col-span-2 mt-2 border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Local wage tax
        </p>
        <p className="col-span-2 -mt-2 text-xs text-slate-400">
          For states with a municipal income/wage tax (OH, PA). Leave the rate at 0% elsewhere.
        </p>

        <WizardField
          form={form}
          name="step6.localWageTaxRate"
          label="Local wage tax rate"
          format="percent"
          min={0}
          max={0.05}
          step={0.001}
        />

        <WizardField
          form={form}
          name="step6.residentTaxableShare"
          label="Resident / taxable share of payroll"
          format="percent"
          min={0}
          max={1}
          step={0.01}
        />

        <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Cost of services
        </p>

        <WizardField
          form={form}
          name="step6.miscTaxesFeesPerWorker"
          label="Misc. taxes & fees per worker ($/yr)"
          format="currency"
          hint="Annual amount per direct worker"
          originChip={chip('miscTaxesFeesPerWorker')}
        />

        <WizardField
          form={form}
          name="step6.householdTaxableSpendingPerWorker"
          label="Household taxable spending per relocating worker ($/yr)"
          format="currency"
          originChip={chip('householdTaxableSpendingPerWorker')}
        />

        <WizardField
          form={form}
          name="step6.utilityFranchiseFeePerJob"
          label="Utility franchise fee per job ($/yr)"
          format="currency"
          originChip={chip('utilityFranchiseFeePerJob')}
        />

        <WizardField
          form={form}
          name="step6.baseCityCostPerWorker"
          label="City cost of services per worker ($/yr)"
          format="currency"
          originChip={chip('baseCityCostPerWorker')}
        />

        <WizardField
          form={form}
          name="step6.baseCountyCostPerWorker"
          label="County cost of services per worker ($/yr)"
          format="currency"
          originChip={chip('baseCountyCostPerWorker')}
        />

        <WizardField
          form={form}
          name="step6.baseIsdCostPerStudent"
          label="ISD cost per new student ($/yr)"
          format="currency"
          originChip={chip('baseIsdCostPerStudent')}
        />

        <WizardField
          form={form}
          name="step6.costInflationRate"
          label="Cost inflation rate"
          format="percent"
          min={0}
          max={0.2}
          step={0.005}
          originChip={chip('costInflationRate')}
        />

        {/* ADR-036: jurisdiction tier presence */}
        <p className="col-span-2 mt-2 border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Jurisdiction tiers
        </p>
        <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700 sm:col-span-1">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            {...form.register('step6.hasCountyPropertyTax')}
          />
          County property tax applies
        </label>
        <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700 sm:col-span-1">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            {...form.register('step6.hasSeparateSchoolDistrict')}
          />
          Separate school district
        </label>
        <p className="col-span-2 -mt-2 text-xs text-slate-400">
          Uncheck for a town-only or consolidated jurisdiction; that tier is left out of the
          analysis.
        </p>

        {/* District property tax rates */}
        <p className="col-span-2 mt-2 border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
          District property tax rates
        </p>

        <WizardField
          form={form}
          name="step6.countyPropertyTaxRate"
          label="County property tax rate"
          format="percent"
          min={0}
          max={0.05}
          step={0.0001}
          originChip={chip('countyPropertyTaxRate')}
        />

        <WizardField
          form={form}
          name="step6.isdPropertyTaxRate"
          label="ISD property tax rate"
          format="percent"
          min={0}
          max={0.05}
          step={0.0001}
          originChip={chip('isdPropertyTaxRate')}
        />

        {/* ADR-034 2.3: school recapture / local retention */}
        <WizardField
          form={form}
          name="step6.schoolLocalRetentionShare"
          label="ISD local retention share"
          format="percent"
          min={0}
          max={1}
          step={0.01}
          hint="Share of school property tax the district keeps after state recapture (100% = none)"
        />

        {/* Optional special district */}
        <p className="col-span-2 mt-2 border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Special district (optional)
        </p>

        <div className="col-span-2">
          <WizardField
            form={form}
            name="step6.specialDistrictName"
            label="Special district name"
            type="text"
            hint='e.g. "Hill Country WCID #1" — leave blank if none'
          />
        </div>

        {specialDistrictName && (
          <>
            <WizardField
              form={form}
              name="step6.specialDistrictPropertyTaxRate"
              label="Special district property tax rate"
              format="percent"
              min={0}
              max={0.05}
              step={0.0001}
            />

            <WizardField
              form={form}
              name="step6.specialDistrictCostPerWorker"
              label="Special district cost per worker ($/yr)"
              format="currency"
              hint="Annual operating cost per direct worker"
            />
          </>
        )}

        {/* ADR-034: taxability toggles (2.2) */}
        <p className="col-span-2 mt-2 border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Taxability
        </p>
        <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700 sm:col-span-1">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            {...form.register('step6.taxesBpp')}
          />
          Business personal property is taxed
        </label>
        <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700 sm:col-span-1">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            {...form.register('step6.taxesInventory')}
          />
          Inventory is taxed
        </label>
        <p className="col-span-2 -mt-2 text-xs text-slate-400">
          Uncheck a class your state exempts; the adapter removes it from the property tax base.
        </p>

        {/* ADR-034: assessment ratios (2.4) */}
        <p className="col-span-2 mt-2 border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Assessment ratios
        </p>
        <p className="col-span-2 -mt-2 text-xs text-slate-400">
          Share of market value that is taxable. 100% = assessed equals market (Texas). Classified
          states use a lower ratio (e.g. 35%).
        </p>

        <WizardField
          form={form}
          name="step6.assessmentRatioReal"
          label="Real property assessment ratio"
          format="percent"
          min={0}
          max={1}
          step={0.01}
        />

        <WizardField
          form={form}
          name="step6.assessmentRatioBpp"
          label="BPP assessment ratio"
          format="percent"
          min={0}
          max={1}
          step={0.01}
        />

        <WizardField
          form={form}
          name="step6.assessmentRatioInventory"
          label="Inventory assessment ratio"
          format="percent"
          min={0}
          max={1}
          step={0.01}
        />
      </div>
    </div>
  );
}
